import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  encryptText,
  encryptTextWithKey,
  decryptText,
  unwrapAesKey,
} from "../helpers/licenseImageCrypto";
import { wrapKeyForAllRecipients } from "../helpers/licenseSharing";

/**
 * LicenseNumberCard – end-to-end encrypted driver's license number.
 *
 * The number is AES-GCM encrypted in the browser; the AES key is RSA-wrapped
 * for every user authorized for license access (admins + members of a group
 * with license access) on upload. The server never sees the plaintext.
 *
 * Props:
 *   - licenseId       {number|string}
 *   - apiUrl          {string}
 *   - legacyPlaintext {string|null}  old unencrypted number, if any
 *   - privateKey      {CryptoKey|null}
 *   - hasServerKey    {boolean}      current user has a public key on the server
 *   - currentUserId   {number}
 */
function LicenseNumberCard({ licenseId, apiUrl, legacyPlaintext, privateKey, hasServerKey, currentUserId }) {
  const [meta, setMeta] = useState(null); // { exists, hasAccess, isOwner, sharedWith? }
  const [value, setValue] = useState(null); // decrypted number
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const authHeader = () => ({ accessToken: localStorage.getItem("accessToken") });

  const loadMeta = useCallback(async () => {
    try {
      const { data } = await axios.get(`${apiUrl}/licenses/${licenseId}/number/meta`, { headers: authHeader() });
      setMeta(data);
    } catch {
      setMeta({ exists: false });
    }
  }, [apiUrl, licenseId]);

  useEffect(() => {
    if (apiUrl && licenseId) loadMeta();
  }, [apiUrl, licenseId, loadMeta]);

  // Store the encrypted number for the first time (or migrate legacy plaintext),
  // automatically shared with every authorized user.
  const storeNumber = async (plain) => {
    if (!plain) return;
    setBusy(true); setError(null); setInfo(null);
    try {
      const { ciphertextBase64, ivBase64, aesKey } = await encryptText(plain);
      const keys = await wrapKeyForAllRecipients(aesKey, apiUrl, currentUserId);
      await axios.post(
        `${apiUrl}/licenses/${licenseId}/number`,
        { ciphertext: ciphertextBase64, iv: ivBase64, keys },
        { headers: authHeader() }
      );
      setValue(plain);
      setDraft("");
      setEditing(false);
      await loadMeta();
      setInfo(`Führerscheinnummer verschlüsselt gespeichert und mit ${keys.length} Benutzer(n) geteilt.`);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Fehler beim Speichern.");
    } finally {
      setBusy(false);
    }
  };

  const revealNumber = async () => {
    if (value !== null) { setValue(null); return; }
    setBusy(true); setError(null);
    try {
      const { data } = await axios.get(`${apiUrl}/licenses/${licenseId}/number`, { headers: authHeader() });
      const aesKey = await unwrapAesKey(data.envelope, privateKey);
      setValue(await decryptText(data.ciphertext, data.iv, aesKey));
    } catch (err) {
      setError(err.response?.data?.error || "Entschlüsselung fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  // Edit keeps the same AES key, so the existing envelopes stay valid.
  const saveEdit = async () => {
    const next = draft.trim();
    if (!next) return;
    setBusy(true); setError(null); setInfo(null);
    try {
      const { data } = await axios.get(`${apiUrl}/licenses/${licenseId}/number`, { headers: authHeader() });
      const aesKey = await unwrapAesKey(data.envelope, privateKey, { usages: ["encrypt", "decrypt"] });
      const { ciphertextBase64, ivBase64 } = await encryptTextWithKey(next, aesKey);
      await axios.put(
        `${apiUrl}/licenses/${licenseId}/number`,
        { ciphertext: ciphertextBase64, iv: ivBase64 },
        { headers: authHeader() }
      );
      setValue(next);
      setEditing(false);
      setInfo("Führerscheinnummer aktualisiert.");
    } catch (err) {
      setError(err.response?.data?.error || "Fehler beim Aktualisieren.");
    } finally {
      setBusy(false);
    }
  };

  // Re-wrap the AES key for every currently authorized user (e.g. after a new
  // colleague got license access or created their key pair).
  const resyncAccess = async () => {
    setBusy(true); setError(null); setInfo(null);
    try {
      const { data } = await axios.get(`${apiUrl}/licenses/${licenseId}/number`, { headers: authHeader() });
      const aesKey = await unwrapAesKey(data.envelope, privateKey, { extractable: true, usages: ["decrypt"] });
      const keys = await wrapKeyForAllRecipients(aesKey, apiUrl);
      const { data: res } = await axios.post(
        `${apiUrl}/licenses/${licenseId}/number/share`,
        { keys },
        { headers: authHeader() }
      );
      await loadMeta();
      setInfo(`Zugriff für ${res.shared} Benutzer aktualisiert.`);
    } catch (err) {
      setError(err.response?.data?.error || "Fehler beim Aktualisieren des Zugriffs.");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (userId) => {
    setBusy(true); setError(null);
    try {
      await axios.delete(`${apiUrl}/licenses/${licenseId}/number/share/${userId}`, { headers: authHeader() });
      await loadMeta();
    } catch (err) {
      setError(err.response?.data?.error || "Fehler beim Entziehen.");
    } finally {
      setBusy(false);
    }
  };

  // ── render ─────────────────────────────────────────────────────────────────

  if (!hasServerKey) {
    return (
      <div className="form-group">
        <label>Führerscheinnummer:</label>
        <p style={{ fontSize: "0.85rem", color: "#6c757d", margin: 0 }}>
          🔒 Verschlüsselt. Zum Anzeigen oder Hinterlegen bitte zuerst einen{" "}
          <a href="/profile">Verschlüsselungsschlüssel im Profil</a> erstellen.
        </p>
      </div>
    );
  }

  if (!meta) return <div className="form-group"><label>Führerscheinnummer:</label><p style={{ margin: 0 }}>Wird geladen…</p></div>;

  return (
    <div className="form-group">
      <label>Führerscheinnummer (Ende-zu-Ende verschlüsselt):</label>

      {error && <p style={{ color: "#dc3545", fontSize: "0.8rem", margin: "0.25rem 0" }}>{error}</p>}
      {info && <p style={{ color: "#28a745", fontSize: "0.8rem", margin: "0.25rem 0" }}>{info}</p>}

      {/* Not stored yet */}
      {!meta.exists && (
        <div>
          {legacyPlaintext ? (
            <p style={{ fontSize: "0.85rem", margin: "0 0 0.4rem" }}>
              Aktuell unverschlüsselt gespeichert: <code>{legacyPlaintext}</code>
              <button type="button" className="btn btn-primary btn-sm" style={{ marginLeft: "0.5rem" }}
                onClick={() => storeNumber(legacyPlaintext)} disabled={busy}>
                🔒 Jetzt verschlüsseln
              </button>
            </p>
          ) : (
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
              <input className="form-control" style={{ maxWidth: "240px" }} value={draft}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); storeNumber(draft.trim()); } }}
                onChange={(e) => setDraft(e.target.value)} placeholder="z. B. B123456789" disabled={busy} />
              <button type="button" className="btn btn-primary btn-sm" onClick={() => storeNumber(draft.trim())}
                disabled={busy || !draft.trim()}>
                Verschlüsselt speichern
              </button>
            </div>
          )}
          <p style={{ fontSize: "0.75rem", color: "#6c757d", margin: "0.3rem 0 0" }}>
            Wird automatisch mit allen Benutzern mit Führerschein- oder Admin-Berechtigung geteilt.
          </p>
        </div>
      )}

      {/* Stored, no access */}
      {meta.exists && !meta.hasAccess && (
        <p style={{ fontSize: "0.85rem", color: "#6c757d", margin: 0 }}>
          🔒 Verschlüsselt – Sie haben keinen Zugriff. Der Eigentümer kann den Zugriff aktualisieren.
        </p>
      )}

      {/* Stored, has access */}
      {meta.exists && meta.hasAccess && (
        <div>
          {!privateKey ? (
            <p style={{ fontSize: "0.85rem", color: "#6c757d", margin: 0 }}>
              🔒 Verschlüsselt. Bitte oben den Private Key laden, um die Nummer anzuzeigen.
            </p>
          ) : editing ? (
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
              <input className="form-control" style={{ maxWidth: "240px" }} value={draft}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveEdit(); } }}
                onChange={(e) => setDraft(e.target.value)} disabled={busy} />
              <button type="button" className="btn btn-primary btn-sm" onClick={saveEdit} disabled={busy || !draft.trim()}>Speichern</button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditing(false)} disabled={busy}>Abbrechen</button>
            </div>
          ) : (
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
              <button type="button" className="btn btn-secondary btn-sm" onClick={revealNumber} disabled={busy}>
                {value !== null ? "Ausblenden" : "🔓 Anzeigen"}
              </button>
              {value !== null && <code style={{ fontSize: "0.95rem" }}>{value}</code>}
              {value !== null && (
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setDraft(value); setEditing(true); }} disabled={busy}>
                  Bearbeiten
                </button>
              )}
            </div>
          )}

          {/* Access management (owner only) */}
          {meta.isOwner && privateKey && (
            <div style={{ marginTop: "0.75rem", padding: "0.6rem", background: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: "4px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.5rem" }}>
                <strong style={{ fontSize: "0.8rem" }}>
                  Zugriff: {1 + (meta.sharedWith || []).length} Benutzer
                </strong>
                <button type="button" className="btn btn-secondary btn-sm" onClick={resyncAccess} disabled={busy}>
                  🔄 Zugriff aktualisieren
                </button>
              </div>
              <p style={{ fontSize: "0.72rem", color: "#6c757d", margin: "0.3rem 0 0" }}>
                „Zugriff aktualisieren" gibt die Nummer für alle aktuell Berechtigten frei (z. B. neue Kollegen).
              </p>
              {(meta.sharedWith || []).length > 0 && (
                <ul style={{ listStyle: "none", padding: 0, margin: "0.4rem 0 0", fontSize: "0.82rem" }}>
                  {meta.sharedWith.map((s) => (
                    <li key={s.userId} style={{ display: "flex", justifyContent: "space-between", padding: "0.2rem 0" }}>
                      <span>{s.fullName || s.username}</span>
                      <button type="button" style={{ background: "none", border: "none", color: "#dc3545", cursor: "pointer", padding: 0 }}
                        onClick={() => revoke(s.userId)} disabled={busy}>entziehen</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default LicenseNumberCard;
